import { observer } from "mobx-react";
import * as Sentry from "@sentry/react";
import { useTranslation } from "react-i18next";
import { useCallback, useContext, useEffect, useMemo } from "react";
import { RootStoreContext } from "src/stores/RootStore";
import { UI } from "@wwimmo/ui";
import styles from "./AgendaVotingOptions.module.css";

const MAX_INPUTS = 5;

const AgendaVotingOptionsBase = () => {
    const { t } = useTranslation();
    const { assemblyStore, votingStore } = useContext(RootStoreContext);

    useEffect(() => {
        if (assemblyStore.currentAssemblyRealestates.length > 0 && votingStore.selectedRealestateIds.length === 0) {
            const allIds = assemblyStore.currentAssemblyRealestates.map((re) => re.realestate.id);
            votingStore.setSelectedRealestateIds(allIds);
        }
    }, [assemblyStore.currentAssemblyRealestates, votingStore]);

    const isNew = votingStore.isNewVoting;
    const options = isNew ? votingStore.votingOptionsDefault : votingStore.votingOptions.map((o) => o.text);
    const canAdd = options.length < MAX_INPUTS;

    const updateOption = useCallback(
        (index: number, value: string) => {
            if (isNew) {
                const updated = [...votingStore.votingOptionsDefault];
                updated[index] = value;
                votingStore.setVotingOptionsDefault(updated);
            } else {
                const updated = [...votingStore.votingOptions];
                updated[index] = { ...updated[index], text: value };
                votingStore.setVotingOptions(updated);
            }
        },
        [votingStore, isNew]
    );

    const removeOption = useCallback(
        (index: number) => {
            if (isNew) {
                const updated = votingStore.votingOptionsDefault.filter((_, i) => i !== index);
                votingStore.setVotingOptionsDefault(updated);
            } else {
                const updated = votingStore.votingOptions.filter((_, i) => i !== index);
                votingStore.setVotingOptions(updated);
            }
        },
        [votingStore, isNew]
    );

    const addOption = useCallback(() => {
        if (isNew) {
            const updated = [...votingStore.votingOptionsDefault, ""];
            votingStore.setVotingOptionsDefault(updated);
        } else {
            const updated = [...votingStore.votingOptions, { votingoptionid: "", text: "" }];
            votingStore.setVotingOptions(updated);
        }
    }, [votingStore, isNew]);

    const handlers = useMemo(
        () =>
            options.map((_, index) => ({
                onChange: (e: React.ChangeEvent<HTMLInputElement>) => updateOption(index, e.target.value),
                onClick: () => removeOption(index)
            })),
        [updateOption, removeOption, options]
    );

    const onCheckboxChange = useCallback(
        (realestateId: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
            const selected = votingStore.selectedRealestateIds;
            const updated = e.target.checked
                ? [...selected, realestateId] // add if checked
                : selected.filter((id) => id !== realestateId); // remove if unchecked

            votingStore.setSelectedRealestateIds(updated);
        },
        [votingStore]
    );

    return (
        <>
            <UI.Col>
                <div className={styles.selectionTitle}>{t("labels.selection")}</div>
                <div>
                    {options.map((option, index) => (
                        <div key={index} className={styles.inputRow}>
                            <input
                                className={styles.input}
                                value={option}
                                onChange={handlers[index].onChange}
                                placeholder="Placeholder"
                            />
                            <UI.Icon
                                icon={UI.SVGIcon.Close}
                                size={20}
                                className={styles.removeButton}
                                onClick={handlers[index].onClick}
                            />
                        </div>
                    ))}

                    {canAdd && (
                        <div className={styles.inputRow}>
                            <div className={styles.addButton} onClick={addOption}>
                                <UI.Icon icon={UI.SVGIcon.Add} size={12} className={styles.plus} />
                                {t("labels.add")}
                            </div>
                        </div>
                    )}
                </div>
            </UI.Col>
            <UI.Col>
                <div className={styles.checkboxCard}>
                    <div className={styles.checkboxTitle}>{t("labels.communion")}</div>
                    <div className={styles.checkboxGroup}>
                        {assemblyStore.currentAssemblyRealestates.map((realestate) => {
                            return (
                                <UI.Checkbox
                                    key={realestate.realestate.id}
                                    name={realestate.realestate.id}
                                    label={realestate.realestate.name}
                                    checked={votingStore.selectedRealestateIds.includes(realestate.realestate.id)}
                                    onChange={onCheckboxChange(realestate.realestate.id)}
                                />
                            );
                        })}
                    </div>
                </div>
            </UI.Col>
        </>
    );
};

export const AgendaVotingOptions = Sentry.withProfiler(observer(AgendaVotingOptionsBase));
